import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { Row, Form, Col, Button, Alert, Badge } from "react-bootstrap";
import { TbCalendarStar } from "react-icons/tb";
import { IoMdAdd } from "react-icons/io";

export default function EditAtd() {
  const { attendanceId } = useParams();
  const navigate = useNavigate();
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [date, setDate] = useState("");
  const [workedHours, setWorkedHours] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [userId, setUserId] = useState("");
  const [shiftStart, setShiftStart] = useState("");
  const [shiftEnd, setShiftEnd] = useState("");
  const [lateMinutes, setLateMinutes] = useState(0);
  const [earlyCloseMinutes, setEarlyCloseMinutes] = useState(0);
  const [extraMinutes, setExtraMinutes] = useState(0);

  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        // Fetch attendance record
        const attendanceRes = await fetch(`http://localhost:5544/api/attendance/attendance/${attendanceId}`);
        const attendanceData = await attendanceRes.json();
        
        if (attendanceData.success) {
          const { checkIn, checkOut, date, userId } = attendanceData.data;
          setStartTime(moment(checkIn).format("HH:mm"));
          setEndTime(checkOut ? moment(checkOut).format("HH:mm") : "");
          setDate(moment(date).format("YYYY-MM-DD"));
          setUserId(userId);

          // ✅ Fetch user's shift timings
          const timingRes = await fetch(`http://localhost:5544/api/timing/user/${userId}`);
          const timingData = await timingRes.json();
          
          if (timingData.success) {
            setShiftStart(timingData.data.shiftStart); // e.g., "14:00"
            setShiftEnd(timingData.data.shiftEnd);     // e.g., "22:00"
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchAttendanceData();
  }, [attendanceId]);

  useEffect(() => {
    if (startTime && endTime && date) {
      // Calculate worked hours
      const start = moment(`${date} ${startTime}`, "YYYY-MM-DD HH:mm");
      const end = moment(`${date} ${endTime}`, "YYYY-MM-DD HH:mm");
      let duration = end.diff(start, "minutes");

      if (duration < 0) {
        duration = end.add(1, "day").diff(start, "minutes"); // Handle overnight
      }
      setWorkedHours(`${Math.floor(duration / 60)} hr ${duration % 60} min`);

      // ✅ Calculate late/early/extra minutes (if shift timings exist)
      if (shiftStart && shiftEnd) {
        const officialStart = moment(`${date} ${shiftStart}`, "YYYY-MM-DD HH:mm");
        const officialEnd = moment(`${date} ${shiftEnd}`, "YYYY-MM-DD HH:mm");

        setLateMinutes(Math.max(0, start.diff(officialStart, "minutes")));
        setEarlyCloseMinutes(Math.max(0, officialEnd.diff(end, "minutes")));
        setExtraMinutes(Math.max(0, end.diff(officialEnd, "minutes")));
      }
    }
  }, [startTime, endTime, date, shiftStart, shiftEnd]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`http://localhost:5544/api/attendance/attendance/edit/${attendanceId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          checkIn: startTime,
          checkOut: endTime,
          date,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setShowAlert(true);
        setTimeout(() => navigate("/yesterday-atd"), 2000);
      } else {
        alert("Error updating attendance.");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <div className="container-fluid">
      <h1 className="add-attendance-title"><TbCalendarStar /> Edit Attendance</h1>
      <hr />
      <div className="add-attendance-container">
        {showAlert && (
          <Alert variant="success" onClose={() => setShowAlert(false)} dismissible>
            Attendance updated successfully! Redirecting...
          </Alert>
        )}

        {/* Shift Timings (Optional) */}
        {shiftStart && shiftEnd && (
          <Alert variant="info">
            <strong>Official Shift:</strong> {shiftStart} to {shiftEnd}
            {lateMinutes > 0 && <Badge bg="warning" className="ms-2">Late: {lateMinutes}m</Badge>}
            {earlyCloseMinutes > 0 && <Badge bg="danger" className="ms-2">Early: {earlyCloseMinutes}m</Badge>}
            {extraMinutes > 0 && <Badge bg="success" className="ms-2">Overtime: {extraMinutes}m</Badge>}
          </Alert>
        )}

        <Row className="mb-3">
          <Form.Group as={Col} xs={12} md={3}>
            <Form.Label>Date <span className="required">*</span>:</Form.Label>
            <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Form.Group>
          <Form.Group as={Col} xs={12} md={3}>
            <Form.Label>Start Time <span className="required">*</span>:</Form.Label>
            <Form.Control type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </Form.Group>
          <Form.Group as={Col} xs={12} md={3}>
            <Form.Label>End Time <span className="required">*</span>:</Form.Label>
            <Form.Control type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} xs={12} md={3}>
            <Form.Label>Worked Hours:</Form.Label>
            <Form.Control type="text" value={workedHours} readOnly />
          </Form.Group>
        </Row>
        <Button className="btn-primary" onClick={handleSubmit}>
          <IoMdAdd /> Update Attendance
        </Button>
      </div>
    </div>
  );
}